/**
 * Runtime-safe font preload for the two Indic script fonts.
 *
 * ── Why runtime and not `<link rel="preload">` in index.html?
 * `@fontsource-variable/*` files are emitted with content hashes by
 * webpack (e.g. `noto-sans-devanagari-devanagari-wght-normal.e47ca…woff2`).
 * A hardcoded preload URL in `index.html` would break on every rebuild.
 * By resolving the module URL through webpack we get the same hashed
 * URL that CSS uses — no duplicate downloads, no stale preload URLs,
 * no "preload-unused" warnings.
 *
 * ── What we preload
 *   • Devanagari primary weight (Hindi content) — only when lang === "hi".
 *   • Gujarati  primary weight (Gujarati content) — only when lang === "gu".
 *   • Nothing for English visitors — the Manrope + system stack renders
 *     English pages fine, so we avoid a needless 60–70 KB transfer.
 *
 * ── Attributes used
 *   • `as="font"`, `type="font/woff2"` — correct MIME for browsers.
 *   • `crossorigin="anonymous"` — required by the Fetch spec for fonts.
 *   • `href` — exact same URL the CSS emits, so the browser reuses the
 *     cached preloaded response (no double fetch).
 *
 * `font-display: swap` is already set by @fontsource-variable, so text
 * paints in a system fallback immediately and swaps to Noto without a
 * layout jump. We do NOT set `font-display` here.
 */

// Webpack resolves these to the same hashed URLs the CSS references.
import DEVANAGARI_WGHT from "@fontsource-variable/noto-sans-devanagari/files/noto-sans-devanagari-devanagari-wght-normal.woff2";
import GUJARATI_WGHT from "@fontsource-variable/noto-sans-gujarati/files/noto-sans-gujarati-gujarati-wght-normal.woff2";

const PRELOAD_MAP = {
  hi: [{ href: DEVANAGARI_WGHT, id: "preload-devanagari" }],
  gu: [{ href: GUJARATI_WGHT, id: "preload-gujarati" }],
};

const _created = new Set();

export function preloadFontsFor(lang) {
  if (typeof document === "undefined") return;
  const targets = PRELOAD_MAP[lang];
  if (!targets) return;
  for (const { href, id } of targets) {
    if (_created.has(id) || document.getElementById(id)) {
      _created.add(id);
      continue;
    }
    const link = document.createElement("link");
    link.id = id;
    link.rel = "preload";
    link.as = "font";
    link.type = "font/woff2";
    link.href = href;
    link.crossOrigin = "anonymous";
    document.head.appendChild(link);
    _created.add(id);
  }
}
